/**
 * Internationalization (i18n) Module
 * Supports Thai, English, and Chinese
 */

import type { Language } from './config';

// Translation keys structure
export interface Translations {
  // Common
  appName: string;
  loading: string;
  error: string;
  retry: string;
  back: string;
  next: string;
  confirm: string;
  cancel: string;
  close: string;
  done: string;

  // Search page
  searchTitle: string;
  checkIn: string;
  checkOut: string;
  adults: string;
  children: string;
  rooms: string;
  searchButton: string;

  // Room selection
  roomsTitle: string;
  perNight: string;
  totalFor: string;
  nights: string;
  inclTaxService: string;
  selectRoom: string;
  noRoomsAvailable: string;
  changeSearch: string;
  amenities: string;
  cancellationPolicy: string;
  mealPlan: string;

  // Guest info
  guestInfoTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  // Additional guest fields
  guestTitle: string;
  titleMr: string;
  titleMrs: string;
  titleMs: string;
  titleMiss: string;
  titleDr: string;
  selectTitle: string;
  nationality: string;
  nationalityPlaceholder: string;
  idType: string;
  idTypePassport: string;
  idTypeNationalId: string;
  idTypeDrivingLicense: string;
  idNumber: string;
  idNumberPlaceholder: string;
  selectIdType: string;

  // Confirmation
  confirmTitle: string;
  bookingSummary: string;
  roomType: string;
  guestName: string;
  totalAmount: string;
  confirmBooking: string;

  // Success
  successTitle: string;
  bookingConfirmed: string;
  confirmationNumber: string;
  checkInTime: string;
  checkOutTime: string;
  viewInChat: string;

  // Errors
  errorGeneric: string;
  errorNetwork: string;
  errorNoRooms: string;
  errorBookingFailed: string;

  // Validation
  required: string;
  invalidEmail: string;
  invalidPhone: string;
  selectDates: string;

  // Navigation
  home: string;
  stepSearch: string;
  stepRoom: string;
  stepGuest: string;
  stepConfirm: string;

  // Payment
  payAtHotel: string;

  // My Bookings
  myBookings: string;
  bookings: string;
  noBookings: string;
  noBookingsDesc: string;
  startBooking: string;
  statusConfirmed: string;
  statusCancelled: string;
  statusCompleted: string;
  statusPending: string;

  // Extra Charges
  capacityExceeded: string;
  capacityExceededMessage: string;
  extraAdults: string;
  extraChildren: string;
  person: string;
  personPerNight: string;
  totalExtraCharge: string;
  extraCharges: string;
  roomRate: string;
  roomRateBeforeTax: string;
  serviceCharge10: string;
  vat7: string;

  // Additional details for success page
  ratePlan: string;
  guests: string;

  // Auto-fill
  autoFilledFromPrevious: string;
  editingBooking: string;
  confirmEditBooking: string;
  confirmCreateBooking: string;

  // Room Types Browse Page
  roomTypesTitle: string;
  startingFrom: string;
  checkAvailability: string;
  ourRooms: string;
  excludingTaxes: string;

  // Payment
  paymentTitle: string;
  payWithCard: string;
  payWithPromptPay: string;
  processingPayment: string;
  paymentSuccessful: string;
  paymentFailed: string;
  paymentError: string;
  retryPayment: string;
  skipPayment: string;
  payNow: string;
  paymentWarningBanner: string;
  paymentNoGoBack: string;
  paymentBackWarning: string;
  statusPaid: string;

  // Booking Management
  editBooking: string;
  cancelBooking: string;
  cannotModifyBooking: string;
  saveChanges: string;
  confirmCancelBooking: string;
  keepBooking: string;
  confirmCancel: string;
  bookingUpdatedSuccess: string;
  bookingCancelledSuccess: string;
  errorLoadingBooking: string;
  errorUpdatingBooking: string;
  errorCancellingBooking: string;
  pleaseFillAllFields: string;
  specialRequestsPlaceholder: string;
  cancellationPolicy3Days: string;
  bookingWillBeCancelled: string;
  actionCannotBeUndone: string;
  saving: string;
  checkOutMustBeAfterCheckIn: string;
  cannotModifyLessThan3Days: string;
  roomNotAvailable: string;
  bookingNotFound: string;
  connectionError: string;
  nonRefundable: string;
  nonRefundableWarning: string;
  nonRefundableCannotModify: string;
  paidBookingCannotModify: string;
}

// Thai translations
const th: Translations = {
  appName: 'จองห้องพัก',
  loading: 'กำลังโหลด...',
  error: 'เกิดข้อผิดพลาด',
  retry: 'ลองอีกครั้ง',
  back: 'ย้อนกลับ',
  next: 'ถัดไป',
  confirm: 'ยืนยัน',
  cancel: 'ยกเลิก',
  close: 'ปิด',
  done: 'เสร็จสิ้น',

  searchTitle: 'ค้นหาและจอง',
  checkIn: 'วันเช็คอิน',
  checkOut: 'วันเช็คเอาท์',
  adults: 'ผู้ใหญ่',
  children: 'เด็ก',
  rooms: 'ห้อง',
  searchButton: 'ค้นหาและจอง',

  roomsTitle: 'เลือกห้องพัก',
  perNight: '/คืน',
  totalFor: 'รวม',
  inclTaxService: '(รวมภาษี + ค่าบริการ)',
  nights: 'คืน',
  selectRoom: 'เลือกห้องนี้',
  noRoomsAvailable: 'ไม่มีห้องว่างในช่วงเวลาที่เลือก',
  changeSearch: 'แก้ไข',
  amenities: 'สิ่งอำนวยความสะดวก',
  cancellationPolicy: 'นโยบายการยกเลิก',
  mealPlan: 'มื้ออาหาร',

  guestInfoTitle: 'ข้อมูลผู้เข้าพัก',
  firstName: 'ชื่อ',
  lastName: 'นามสกุล',
  email: 'อีเมล',
  phone: 'เบอร์โทรศัพท์',
  specialRequests: 'คำขอพิเศษ (ถ้ามี)',
  guestTitle: 'คำนำหน้า',
  titleMr: 'นาย',
  titleMrs: 'นาง',
  titleMs: 'น.ส.',
  titleMiss: 'น.ส.',
  titleDr: 'ดร.',
  selectTitle: '-- เลือกคำนำหน้า --',
  nationality: 'สัญชาติ',
  nationalityPlaceholder: 'เช่น ไทย, จีน, อังกฤษ',
  idType: 'ประเภทเอกสารประจำตัว',
  idTypePassport: 'หนังสือเดินทาง',
  idTypeNationalId: 'บัตรประชาชน',
  idTypeDrivingLicense: 'ใบขับขี่',
  idNumber: 'เลขที่เอกสาร',
  idNumberPlaceholder: 'กรุณากรอกเลขที่เอกสาร',
  selectIdType: '-- เลือกประเภทเอกสาร --',

  confirmTitle: 'ยืนยันการจอง',
  bookingSummary: 'สรุปการจอง',
  roomType: 'ประเภทห้อง',
  guestName: 'ชื่อผู้เข้าพัก',
  totalAmount: 'ยอดรวมทั้งหมด',
  confirmBooking: 'ยืนยันการจอง',

  successTitle: 'จองสำเร็จ!',
  bookingConfirmed: 'การจองของคุณได้รับการยืนยันแล้ว',
  confirmationNumber: 'หมายเลขยืนยัน',
  checkInTime: 'เวลาเช็คอิน: 14:00 น.',
  checkOutTime: 'เวลาเช็คเอาท์: 12:00 น.',
  viewInChat: 'ดูในแชท LINE',

  errorGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
  errorNetwork: 'ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต',
  errorNoRooms: 'ไม่พบห้องว่างในช่วงเวลาที่เลือก',
  errorBookingFailed: 'ไม่สามารถจองได้ กรุณาลองใหม่',

  required: 'กรุณากรอกข้อมูลนี้',
  invalidEmail: 'อีเมลไม่ถูกต้อง',
  invalidPhone: 'เบอร์โทรศัพท์ไม่ถูกต้อง',
  selectDates: 'กรุณาเลือกวันที่',

  home: 'หน้าแรก',
  stepSearch: 'ค้นหา',
  stepRoom: 'เลือกห้อง',
  stepGuest: 'ข้อมูล',
  stepConfirm: 'ยืนยัน',

  payAtHotel: 'ชำระเงินที่โรงแรม',

  // My Bookings
  myBookings: 'การจองของฉัน',
  bookings: 'รายการ',
  noBookings: 'ยังไม่มีการจอง',
  noBookingsDesc: 'คุณยังไม่มีประวัติการจองห้องพัก',
  startBooking: 'เริ่มจองห้องพัก',
  statusConfirmed: 'ยืนยันแล้ว',
  statusCancelled: 'ยกเลิกแล้ว',
  statusCompleted: 'เข้าพักแล้ว',
  statusPending: 'รอดำเนินการ',

  capacityExceeded: 'จำนวนผู้เข้าพักเกินกำหนด',
  capacityExceededMessage:
    'จำนวนผู้เข้าพักของคุณเกินขีดจำกัดของห้องพัก คุณต้องการชำระค่าบริการเพิ่มหรือไม่?',
  extraAdults: 'ผู้ใหญ่เพิ่ม',
  extraChildren: 'เด็กเพิ่ม',
  person: 'คน',
  personPerNight: 'คน/คืน',
  totalExtraCharge: 'ค่าบริการเพิ่มทั้งหมด',
  extraCharges: 'ค่าบริการเพิ่ม',
  roomRate: 'ค่าห้องพัก',
  roomRateBeforeTax: 'ค่าห้อง (ก่อนภาษี)',
  serviceCharge10: 'ค่าบริการ 10%',
  vat7: 'VAT 7%',
  ratePlan: 'แผนราคา',
  guests: 'ผู้เข้าพัก',
  autoFilledFromPrevious: 'ข้อมูลถูกกรอกอัตโนมัติจากการจองครั้งก่อน',
  editingBooking: 'กำลังแก้ไขการจอง',
  confirmEditBooking: 'ยืนยันการแก้ไขการจองนี้?',
  confirmCreateBooking: 'ยืนยันการจองนี้?',

  // Room Types Browse Page
  roomTypesTitle: 'ประเภทห้องพัก',
  startingFrom: 'เริ่มต้น',
  checkAvailability: 'ตรวจสอบห้องว่าง',
  ourRooms: 'ห้องพักของเรา',
  excludingTaxes: 'ยังไม่รวมภาษีและค่าบริการ',

  // Payment
  paymentTitle: 'ชำระเงิน',
  payWithCard: 'ชำระด้วยบัตรเครดิต/เดบิต',
  payWithPromptPay: 'ชำระด้วย PromptPay',
  processingPayment: 'กำลังประมวลผลการชำระเงิน...',
  paymentSuccessful: 'ชำระเงินสำเร็จ!',
  paymentFailed: 'การชำระเงินล้มเหลว',
  paymentError: 'เกิดข้อผิดพลาดในการชำระเงิน กรุณาลองใหม่',
  retryPayment: 'ลองชำระเงินอีกครั้ง',
  skipPayment: 'ชำระเงินที่โรงแรม',
  payNow: 'ชำระเงินเลย',
  paymentWarningBanner: 'การจองของคุณได้รับการยืนยันแล้ว กรุณาชำระเงินหรือเลือก "ชำระที่โรงแรม"',
  paymentNoGoBack: 'ไม่สามารถย้อนกลับได้',
  paymentBackWarning: 'การจองได้รับการยืนยันแล้ว\nกรุณาชำระเงินหรือเลือก\n"ชำระที่โรงแรม"',
  statusPaid: 'ชำระออนไลน์แล้ว',

  // Booking Management
  editBooking: 'แก้ไขการจอง',
  cancelBooking: 'ยกเลิกการจอง',
  cannotModifyBooking:
    'ไม่สามารถแก้ไขหรือยกเลิกได้ (เหลือเวลาน้อยกว่า 3 วัน) กรุณาติดต่อโรงแรมโดยตรง',
  saveChanges: 'บันทึกการเปลี่ยนแปลง',
  confirmCancelBooking: 'คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?',
  keepBooking: 'เก็บการจอง',
  confirmCancel: 'ยืนยันยกเลิก',
  bookingUpdatedSuccess: 'อัปเดตการจองสำเร็จ',
  bookingCancelledSuccess: 'ยกเลิกการจองสำเร็จ',
  errorLoadingBooking: 'ไม่สามารถโหลดข้อมูลการจองได้',
  errorUpdatingBooking: 'ไม่สามารถอัปเดตการจองได้',
  errorCancellingBooking: 'ไม่สามารถยกเลิกการจองได้',
  pleaseFillAllFields: 'กรุณากรอกข้อมูลให้ครบถ้วน',
  specialRequestsPlaceholder: 'เช่น ต้องการห้องชั้นสูง, เตียงเสริม, ฯลฯ',
  cancellationPolicy3Days: 'ต้องยกเลิกก่อนวันเช็คอินอย่างน้อย 3 วัน',
  bookingWillBeCancelled: 'การจองจะถูกยกเลิกและไม่สามารถกู้คืนได้',
  actionCannotBeUndone: 'การดำเนินการนี้ไม่สามารถย้อนกลับได้!',
  saving: 'กำลังบันทึก',
  checkOutMustBeAfterCheckIn: 'วันเช็คเอาท์ต้องหลังจากวันเช็คอิน',
  cannotModifyLessThan3Days:
    'ไม่สามารถแก้ไขได้ เนื่องจากเหลือเวลาน้อยกว่า 3 วัน กรุณาติดต่อโรงแรมโดยตรง',
  roomNotAvailable: 'ห้องไม่ว่างในวันที่เลือก กรุณาเลือกวันอื่น',
  bookingNotFound: 'ไม่พบข้อมูลการจอง',
  connectionError: 'เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาตรวจสอบอินเทอร์เน็ตและลองใหม่อีกครั้ง',
  nonRefundable: 'นโยบายการคืนเงิน: ไม่สามารถคืนเงินได้',
  nonRefundableWarning: 'ราคานี้เป็นแบบ Non-Refundable ไม่สามารถยกเลิกหรือขอคืนเงินได้ และต้องชำระออนไลน์เท่านั้น',
  nonRefundableCannotModify: 'ราคา Non-Refundable ไม่สามารถแก้ไขหรือยกเลิกได้ กรุณาติดต่อโรงแรมโดยตรง',
  paidBookingCannotModify:
    'การจองนี้ชำระเงินแล้ว หากต้องการแก้ไขหรือยกเลิก กรุณาติดต่อโรงแรมโดยตรง',
};

// English translations
const en: Translations = {
  appName: 'Book a Room',
  loading: 'Loading...',
  error: 'Error',
  retry: 'Try Again',
  back: 'Back',
  next: 'Next',
  confirm: 'Confirm',
  cancel: 'Cancel',
  close: 'Close',
  done: 'Done',

  searchTitle: 'Search & Book',
  checkIn: 'Check-in',
  checkOut: 'Check-out',
  adults: 'Adults',
  children: 'Children',
  rooms: 'Rooms',
  searchButton: 'Search & Book',

  roomsTitle: 'Select a Room',
  perNight: '/night',
  totalFor: 'Total for',
  inclTaxService: '(incl. taxes & service)',
  nights: 'nights',
  selectRoom: 'Select This Room',
  noRoomsAvailable: 'No rooms available for selected dates',
  changeSearch: 'Edit',
  amenities: 'Amenities',
  cancellationPolicy: 'Cancellation Policy',
  mealPlan: 'Meal Plan',

  guestInfoTitle: 'Guest Information',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone Number',
  specialRequests: 'Special Requests (optional)',
  guestTitle: 'Title',
  titleMr: 'Mr.',
  titleMrs: 'Mrs.',
  titleMs: 'Ms.',
  titleMiss: 'Miss',
  titleDr: 'Dr.',
  selectTitle: '-- Select Title --',
  nationality: 'Nationality',
  nationalityPlaceholder: 'e.g. Thai, Chinese, British',
  idType: 'ID Document Type',
  idTypePassport: 'Passport',
  idTypeNationalId: 'National ID',
  idTypeDrivingLicense: 'Driving License',
  idNumber: 'ID Document Number',
  idNumberPlaceholder: 'Enter document number',
  selectIdType: '-- Select document type --',

  confirmTitle: 'Confirm Booking',
  bookingSummary: 'Booking Summary',
  roomType: 'Room Type',
  guestName: 'Guest Name',
  totalAmount: 'Total Amount',
  confirmBooking: 'Confirm Booking',

  successTitle: 'Booking Confirmed!',
  bookingConfirmed: 'Your booking has been confirmed',
  confirmationNumber: 'Confirmation Number',
  checkInTime: 'Check-in Time: 2:00 PM',
  checkOutTime: 'Check-out Time: 12:00 PM',
  viewInChat: 'View in LINE Chat',

  errorGeneric: 'An error occurred. Please try again.',
  errorNetwork: 'Connection failed. Please check your internet.',
  errorNoRooms: 'No rooms found for selected dates.',
  errorBookingFailed: 'Booking failed. Please try again.',

  required: 'This field is required',
  invalidEmail: 'Invalid email address',
  invalidPhone: 'Invalid phone number',
  selectDates: 'Please select dates',

  home: 'Home',
  stepSearch: 'Search',
  stepRoom: 'Room',
  stepGuest: 'Guest',
  stepConfirm: 'Confirm',

  payAtHotel: 'Pay at Hotel',

  // My Bookings
  myBookings: 'My Bookings',
  bookings: 'booking(s)',
  noBookings: 'No Bookings Yet',
  noBookingsDesc: "You don't have any booking history",
  startBooking: 'Start Booking',
  statusConfirmed: 'Confirmed',
  statusCancelled: 'Cancelled',
  statusCompleted: 'Completed',
  statusPending: 'Pending',

  capacityExceeded: 'Capacity Exceeded',
  capacityExceededMessage:
    'The number of guests exceeds the room capacity. Would you like to proceed with extra charges?',
  extraAdults: 'Extra Adults',
  extraChildren: 'Extra Children',
  person: 'person',
  personPerNight: 'person/night',
  totalExtraCharge: 'Total Extra Charge',
  extraCharges: 'Extra Charges',
  roomRate: 'Room Rate',
  roomRateBeforeTax: 'Room Rate (Before Tax)',
  serviceCharge10: 'Service Charge 10%',
  vat7: 'VAT 7%',
  ratePlan: 'Rate Plan',
  guests: 'Guests',
  autoFilledFromPrevious: 'Information auto-filled from your previous booking',
  editingBooking: 'Editing booking',
  confirmEditBooking: 'Confirm booking update?',
  confirmCreateBooking: 'Confirm this booking?',

  // Room Types Browse Page
  roomTypesTitle: 'Room Types',
  startingFrom: 'Starting from',
  checkAvailability: 'Check Availability',
  ourRooms: 'Our Rooms',
  excludingTaxes: 'Excluding taxes & service charge',

  // Payment
  paymentTitle: 'Payment',
  payWithCard: 'Pay with Credit/Debit Card',
  payWithPromptPay: 'Pay with PromptPay',
  processingPayment: 'Processing payment...',
  paymentSuccessful: 'Payment successful!',
  paymentFailed: 'Payment failed',
  paymentError: 'Payment error occurred. Please try again.',
  retryPayment: 'Retry Payment',
  skipPayment: 'Pay at Hotel',
  payNow: 'Pay Now',
  paymentWarningBanner: 'Your booking is confirmed. Please complete payment or choose "Pay at Hotel"',
  paymentNoGoBack: 'Cannot go back',
  paymentBackWarning: 'Booking is confirmed\nPlease pay now or choose\n"Pay at Hotel"',
  statusPaid: 'Paid Online',

  // Booking Management
  editBooking: 'Edit Booking',
  cancelBooking: 'Cancel Booking',
  cannotModifyBooking:
    'Cannot modify or cancel (less than 3 days remaining). Please contact the hotel directly.',
  saveChanges: 'Save Changes',
  confirmCancelBooking: 'Are you sure you want to cancel this booking?',
  keepBooking: 'Keep Booking',
  confirmCancel: 'Confirm Cancellation',
  bookingUpdatedSuccess: 'Booking updated successfully',
  bookingCancelledSuccess: 'Booking cancelled successfully',
  errorLoadingBooking: 'Failed to load booking details',
  errorUpdatingBooking: 'Failed to update booking',
  errorCancellingBooking: 'Failed to cancel booking',
  pleaseFillAllFields: 'Please fill in all required fields',
  specialRequestsPlaceholder: 'e.g. High floor, extra bed, etc.',
  cancellationPolicy3Days: 'Must cancel at least 3 days before check-in',
  bookingWillBeCancelled: 'Booking will be cancelled and cannot be restored',
  actionCannotBeUndone: 'This action cannot be undone!',
  saving: 'Saving',
  checkOutMustBeAfterCheckIn: 'Check-out date must be after check-in date',
  cannotModifyLessThan3Days:
    'Cannot modify booking with less than 3 days remaining. Please contact the hotel directly',
  roomNotAvailable: 'Room not available for selected dates. Please choose different dates',
  bookingNotFound: 'Booking not found',
  connectionError: 'Connection error occurred. Please check your internet and try again',
  nonRefundable: '⛔ Non-Refundable',
  nonRefundableWarning: 'This rate is Non-Refundable. Cancellations cannot be made and online payment is required.',
  nonRefundableCannotModify: 'Non-Refundable rate cannot be modified or cancelled. Please contact the hotel directly.',
  paidBookingCannotModify:
    'This booking has been paid. To modify or cancel, please contact the hotel directly.',
};

// Chinese translations
const zh: Translations = {
  appName: '预订房间',
  loading: '加载中...',
  error: '错误',
  retry: '重试',
  back: '返回',
  next: '下一步',
  confirm: '确认',
  cancel: '取消',
  close: '关闭',
  done: '完成',

  searchTitle: '搜索并预订',
  checkIn: '入住日期',
  checkOut: '退房日期',
  adults: '成人',
  children: '儿童',
  rooms: '房间',
  searchButton: '搜索并预订',

  roomsTitle: '选择房间',
  perNight: '/晚',
  totalFor: '共',
  inclTaxService: '(含税及服务费)',
  nights: '晚',
  selectRoom: '选择此房间',
  noRoomsAvailable: '所选日期无可用房间',
  changeSearch: '编辑',
  amenities: '设施',
  cancellationPolicy: '取消政策',
  mealPlan: '餐饮计划',

  guestInfoTitle: '住客信息',
  firstName: '名',
  lastName: '姓',
  email: '电子邮箱',
  phone: '电话号码',
  specialRequests: '特殊要求（可选）',
  guestTitle: '称谓',
  titleMr: '先生',
  titleMrs: '女士',
  titleMs: '女士',
  titleMiss: '小姐',
  titleDr: '博士',
  selectTitle: '-- 选择称谓 --',
  nationality: '国籍',
  nationalityPlaceholder: '例如: 泰国, 中国, 英国',
  idType: '证件类型',
  idTypePassport: '护照',
  idTypeNationalId: '身份证',
  idTypeDrivingLicense: '驾驶证',
  idNumber: '证件号码',
  idNumberPlaceholder: '请输入证件号码',
  selectIdType: '-- 选择证件类型 --',

  confirmTitle: '确认预订',
  bookingSummary: '预订摘要',
  roomType: '房型',
  guestName: '住客姓名',
  totalAmount: '总金额',
  confirmBooking: '确认预订',

  successTitle: '预订成功！',
  bookingConfirmed: '您的预订已确认',
  confirmationNumber: '确认号',
  checkInTime: '入住时间：下午2:00',
  checkOutTime: '退房时间：中午12:00',
  viewInChat: '在LINE聊天中查看',

  errorGeneric: '发生错误，请重试。',
  errorNetwork: '连接失败，请检查网络。',
  errorNoRooms: '所选日期无房间。',
  errorBookingFailed: '预订失败，请重试。',

  required: '此字段为必填项',
  invalidEmail: '邮箱地址无效',
  invalidPhone: '电话号码无效',
  selectDates: '请选择日期',

  home: '首页',
  stepSearch: '搜索',
  stepRoom: '房间',
  stepGuest: '信息',
  stepConfirm: '确认',

  payAtHotel: '到店付款',

  // My Bookings
  myBookings: '我的预订',
  bookings: '订单',
  noBookings: '暂无预订',
  noBookingsDesc: '您还没有预订记录',
  startBooking: '开始预订',
  statusConfirmed: '已确认',
  statusCancelled: '已取消',
  statusCompleted: '已完成',
  statusPending: '待处理',

  capacityExceeded: '超出容量限制',
  capacityExceededMessage: '客人数量超过房间容量限制。您是否愿意支付额外费用？',
  extraAdults: '额外成人',
  extraChildren: '额外儿童',
  person: '人',
  personPerNight: '人/晚',
  totalExtraCharge: '总额外费用',
  extraCharges: '额外费用',
  roomRate: '房费',
  roomRateBeforeTax: '房费（税前）',
  serviceCharge10: '服务费 10%',
  vat7: '增值税 7%',
  ratePlan: '价格方案',
  guests: '客人',
  autoFilledFromPrevious: '信息已从您上次预订中自动填写',
  editingBooking: '编辑预订',
  confirmEditBooking: '确认更新预订?',
  confirmCreateBooking: '确认此预订?',

  // Room Types Browse Page
  roomTypesTitle: '房型',
  startingFrom: '起价',
  checkAvailability: '查看房间空房',
  ourRooms: '我们的房间',
  excludingTaxes: '不含税费及服务费',

  // Payment
  paymentTitle: '付款',
  payWithCard: '信用卡/借记卡支付',
  payWithPromptPay: 'PromptPay 支付',
  processingPayment: '正在处理付款...',
  paymentSuccessful: '付款成功！',
  paymentFailed: '付款失败',
  paymentError: '付款出错，请重试。',
  retryPayment: '重试付款',
  skipPayment: '到店付款',
  payNow: '立即付款',
  paymentWarningBanner: '您的预订已确认。请完成付款或选择"到店付款"',
  paymentNoGoBack: '无法返回',
  paymentBackWarning: '预订已确认\n请立即付款或选择\n"到店付款"',
  statusPaid: '已在线付款',

  // Booking Management
  editBooking: '编辑预订',
  cancelBooking: '取消预订',
  cannotModifyBooking: '无法修改或取消（少于3天）。请直接联系酒店。',
  saveChanges: '保存更改',
  confirmCancelBooking: '您确定要取消此预订吗？',
  keepBooking: '保留预订',
  confirmCancel: '确认取消',
  bookingUpdatedSuccess: '预订更新成功',
  bookingCancelledSuccess: '预订取消成功',
  errorLoadingBooking: '加载预订详情失败',
  errorUpdatingBooking: '更新预订失败',
  errorCancellingBooking: '取消预订失败',
  pleaseFillAllFields: '请填写所有必填字段',
  specialRequestsPlaceholder: '例如：高楼层、加床等',
  cancellationPolicy3Days: '必须在入住前至少3天取消',
  bookingWillBeCancelled: '预订将被取消且无法恢复',
  actionCannotBeUndone: '此操作无法撤消！',
  saving: '保存中',
  checkOutMustBeAfterCheckIn: '退房日期必须晚于入住日期',
  cannotModifyLessThan3Days: '无法修改预订，剩余时间少于3天。请直接联系酒店',
  roomNotAvailable: '所选日期房间不可用。请选择其他日期',
  bookingNotFound: '未找到预订',
  connectionError: '连接错误。请检查您的网络并重试',
  nonRefundable: '⛔ 不可退款',
  nonRefundableWarning: '此价格为不可退款。不可取消，且仅支持在线支付。',
  nonRefundableCannotModify: '不可退款价格无法修改或取消。请直接联系酒店。',
  paidBookingCannotModify:
    '此预订已付款。如需修改或取消，请直接联系酒店。',
};

// All translations
const translations: Record<Language, Translations> = { th, en, zh };

// Current language (default Thai)
let currentLanguage: Language = 'th';

/**
 * Set the current language
 */
export function setLanguage(lang: Language): void {
  if (translations[lang]) {
    currentLanguage = lang;
  }
}

/**
 * Get the current language
 */
export function getLanguage(): Language {
  return currentLanguage;
}

// Alias for components
export const getCurrentLanguage = getLanguage;

/**
 * Get translation for a key
 */
export function t(key: keyof Translations): string {
  return translations[currentLanguage][key] || translations['en'][key] || key;
}

/**
 * Detect language from LIFF or browser
 */
export function detectLanguage(liffLanguage?: string): Language {
  // Try LIFF language first
  if (liffLanguage) {
    if (liffLanguage.startsWith('th')) return 'th';
    if (liffLanguage.startsWith('zh')) return 'zh';
    if (liffLanguage.startsWith('en')) return 'en';
  }

  // Try browser language
  const browserLang = navigator.language || '';
  if (browserLang.startsWith('th')) return 'th';
  if (browserLang.startsWith('zh')) return 'zh';

  // Default to Thai
  return 'th';
}

/**
 * Format date for display
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  const locale = currentLanguage === 'th' ? 'th-TH' : currentLanguage === 'zh' ? 'zh-CN' : 'en-US';

  return date.toLocaleDateString(locale, options);
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = 'THB'): string {
  const locale = currentLanguage === 'th' ? 'th-TH' : currentLanguage === 'zh' ? 'zh-CN' : 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// =====================================================
// Room Type & Rate Plan Translations
// These are managed by the hotel/SaaS provider
// =====================================================

type LocalizedStrings = Record<Language, string>;

// Room Type translations by code
const roomTypeTranslations: Record<string, LocalizedStrings> = {
  STD: { th: 'ห้องสแตนดาร์ด', en: 'Standard Room', zh: '标准房' },
  SUP: { th: 'ห้องซูพีเรียร์', en: 'Superior Room', zh: '高级房' },
  DLX: { th: 'ห้องดีลักซ์', en: 'Deluxe Room', zh: '豪华房' },
  STE: { th: 'ห้องสวีท', en: 'Suite', zh: '套房' },
  RIV: { th: 'ห้องวิวแม่น้ำ', en: 'River View Room', zh: '河景房' },
  EXE: { th: 'ห้องเอ็กเซ็กคิวทีฟ', en: 'Executive Room', zh: '行政房' },
};

// Rate Plan translations by code
const ratePlanTranslations: Record<string, LocalizedStrings> = {
  RO: { th: 'ราคาห้องพักอย่างเดียว', en: 'Room Only', zh: '仅住宿' },
  BB: { th: 'รวมอาหารเช้า', en: 'Bed & Breakfast', zh: '含早餐' },
  PROMO: { th: 'โปรโมชั่นพิเศษ', en: 'Special Promotion', zh: '特别促销' },
  BIZ: { th: 'แพ็คเกจธุรกิจ', en: 'Business Package', zh: '商务套餐' },
};

// Rate Plan descriptions by code
const ratePlanDescriptions: Record<string, LocalizedStrings> = {
  RO: { th: 'ราคาห้องพักไม่รวมอาหารเช้า', en: 'Room rate without breakfast', zh: '房价不含早餐' },
  BB: {
    th: 'ราคาห้องพักรวมอาหารเช้าบุฟเฟ่ต์',
    en: 'Room rate including buffet breakfast',
    zh: '房价含自助早餐',
  },
  PROMO: {
    th: 'ราคาพิเศษ จองล่วงหน้า 7 วัน รวมอาหารเช้า',
    en: 'Special rate - book 7 days in advance, includes breakfast',
    zh: '特价 - 提前7天预订，含早餐',
  },
  BIZ: {
    th: 'รวมอาหารเช้า WiFi ความเร็วสูง และบริการซักรีด',
    en: 'Includes breakfast, high-speed WiFi, and laundry service',
    zh: '含早餐、高速WiFi和洗衣服务',
  },
};

// Amenity translations
const amenityTranslations: Record<string, LocalizedStrings> = {
  WIFI: { th: 'WiFi ฟรี', en: 'Free WiFi', zh: '免费WiFi' },
  TV: { th: 'ทีวี', en: 'TV', zh: '电视' },
  MINIBAR: { th: 'มินิบาร์', en: 'Minibar', zh: '迷你吧' },
  SAFE: { th: 'ตู้นิรภัย', en: 'Safe', zh: '保险箱' },
  AC: { th: 'เครื่องปรับอากาศ', en: 'Air Conditioning', zh: '空调' },
  HAIRDRYER: { th: 'ไดร์เป่าผม', en: 'Hair Dryer', zh: '吹风机' },
  BATHTUB: { th: 'อ่างอาบน้ำ', en: 'Bathtub', zh: '浴缸' },
  SOFA: { th: 'โซฟา', en: 'Sofa', zh: '沙发' },
  JACUZZI: { th: 'จากุซซี่', en: 'Jacuzzi', zh: '按摩浴缸' },
  LIVING_ROOM: { th: 'ห้องนั่งเล่น', en: 'Living Room', zh: '客厅' },
  BALCONY: { th: 'ระเบียง', en: 'Balcony', zh: '阳台' },
  DESK: { th: 'โต๊ะทำงาน', en: 'Work Desk', zh: '办公桌' },
  LOUNGE_ACCESS: { th: 'เข้า Executive Lounge', en: 'Lounge Access', zh: '行政酒廊' },
};

// Room description translations by room type code
// roomDescriptions hardcode removed — descriptions now come from Firestore via mock-pms


/**
 * Get translated room type name
 */
export function translateRoomType(code: string, fallback: string): string {
  const translation = roomTypeTranslations[code];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return fallback;
}

/**
 * Get translated rate plan name
 */
export function translateRatePlan(code: string, fallback: string): string {
  const translation = ratePlanTranslations[code];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return fallback;
}

/**
 * Get translated rate plan description
 */
export function translateRatePlanDescription(code: string, fallback?: string): string {
  if (fallback && fallback.trim() !== '') {
    return fallback;
  }
  const translation = ratePlanDescriptions[code];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return fallback || '';
}

// Dynamic custom amenities map
interface CustomAmenityItem {
  icon: string;
  name: Record<string, string>;
}
let customAmenitiesMap: Record<string, CustomAmenityItem> = {};

export function setCustomAmenities(amenities: Array<{ code: string; icon: string; name: Record<string, string> }>) {
  customAmenitiesMap = {};
  if (Array.isArray(amenities)) {
    for (const item of amenities) {
      if (item && item.code) {
        customAmenitiesMap[item.code.toUpperCase()] = {
          icon: item.icon,
          name: item.name || {},
        };
      }
    }
  }
}

export function getCustomAmenityIcon(code: string): string | undefined {
  return customAmenitiesMap[code.toUpperCase()]?.icon;
}

/**
 * Get translated amenity name
 */
export function translateAmenity(code: string): string {
  const upperCode = code.toUpperCase();
  const custom = customAmenitiesMap[upperCode];
  if (custom && custom.name) {
    return custom.name[currentLanguage] || custom.name.en || code;
  }
  const translation = amenityTranslations[upperCode];
  if (translation) {
    return translation[currentLanguage] || translation.en;
  }
  return code;
}

/**
 * Get translated room description
 */
export function translateRoomDescription(_roomTypeCode: string, fallback?: string): string {
  // Always use the description from mock-pms (Firestore) — no local hardcode override
  return fallback || '';
}
